import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";
import type { CustomerVerificationStep } from "../types";

export interface VerifiedCustomerState {
    stepHistory: CustomerVerificationStep[];
    currentStep: CustomerVerificationStep;
}

export interface VerifiedCustomerStore {
    verifiedCustomers: Record<string, VerifiedCustomerState>;
    getCurrentStep: (sessionId: string) => CustomerVerificationStep;
    getStepHistory: (sessionId: string) => CustomerVerificationStep[];
    pushStep: (sessionId: string, step: CustomerVerificationStep) => void;
    popStep: (sessionId: string) => void;
    resetSteps: (sessionId: string) => void;
}
const initialStep: CustomerVerificationStep = "CustomerPhoneInput";

export const useVerificationFlowStore = createWithEqualityFn<VerifiedCustomerStore>()(
    persist(
        immer(
            devtools(
                (set, get) => ({
                    verifiedCustomers: {},
                    getCurrentStep(sessionId: string): CustomerVerificationStep {
                        return get().verifiedCustomers[sessionId]?.currentStep || initialStep;
                    },
                    getStepHistory(sessionId: string): CustomerVerificationStep[] {
                        return get().verifiedCustomers[sessionId]?.stepHistory || [initialStep];
                    },
                    pushStep: (sessionId: string, step: CustomerVerificationStep) =>
                        set(
                            (state) => {
                                const verifiedCustomer = state.verifiedCustomers[sessionId] ?? {
                                    currentStep: initialStep,
                                    stepHistory: [initialStep],
                                };
                                verifiedCustomer.stepHistory.push(step);
                                verifiedCustomer.currentStep = step;
                                state.verifiedCustomers[sessionId] = verifiedCustomer;
                            },
                            false,
                            "pushStep",
                        ),
                    popStep: (sessionId: string) =>
                        set(
                            (state) => {
                                const verifiedCustomer = state.verifiedCustomers[sessionId];
                                if (verifiedCustomer && verifiedCustomer.stepHistory.length > 1) {
                                    verifiedCustomer.stepHistory.pop();
                                    verifiedCustomer.currentStep =
                                        verifiedCustomer.stepHistory[verifiedCustomer.stepHistory.length - 1];
                                }
                            },
                            false,
                            "popStep",
                        ),
                    resetSteps: (sessionId: string) =>
                        set(
                            (state) => {
                                delete state.verifiedCustomers[sessionId];
                            },
                            false,
                            "resetSteps",
                        ),
                }),
                {
                    enabled: import.meta.env.VITE_ENV_TYPE !== "production",
                    store: "verification-flow-store",
                    name: "workspace/customer-panel",
                },
            ),
        ),
        { name: "verification-flow-store", skipHydration: true },
    ),
    shallow,
);
